'use client'

import { X } from 'lucide-react'
import { GRADE_COLORS, TOOL_GROUPS } from '@/lib/radar/scoring'
import { CATEGORIES } from '@/lib/radar/categories'

export type ToolGroupKey = 'all' | keyof typeof TOOL_GROUPS

export interface DashboardFilters {
  group: ToolGroupKey
  grade: string | null
  /**
   * Category key from the summary cards, or null. Set by clicking a card
   * rather than by a chip in this bar — the cards ARE the category control,
   * and a second set of five chips here would be the same choice twice.
   * The bar only surfaces it so it can be cleared.
   */
  category?: string | null
}

export const EMPTY_FILTERS: DashboardFilters = {
  group: 'all',
  grade: null,
  category: null,
}

/**
 * Persistent filter chip bar above the metric cards.
 *
 * Two dimensions:
 *  - Tool group: All | Infrastructure | Monitoring (single-select)
 *  - Grade: A / B / C / D / F (single-select, syncs with the in-card
 *    grade filter on the Issues Distribution donut)
 *
 * Filter state is persisted to localStorage by the parent. When any
 * filter is active, the bar shows the count of matching tools and a
 * "Clear" button. Matches the always-visible filter chip pattern from
 * enterprise dashboards (Profound, Ahrefs) while keeping the Radar
 * voice — compact, no chrome, brand-color accents.
 */
export function DashboardFilterBar({
  filters,
  onFiltersChange,
  matchedCount,
  totalCount,
}: {
  filters: DashboardFilters
  onFiltersChange: (next: DashboardFilters) => void
  matchedCount: number
  totalCount: number
}) {
  const activeCategory = filters.category
    ? CATEGORIES.find(c => c.key === filters.category)
    : undefined
  const isFiltered =
    filters.group !== 'all' || filters.grade !== null || !!activeCategory

  const groupOptions: { key: ToolGroupKey; label: string; color: string }[] = [
    { key: 'all', label: 'All tools', color: 'var(--muted-foreground)' },
    {
      key: 'infrastructure',
      label: TOOL_GROUPS.infrastructure.label,
      color: TOOL_GROUPS.infrastructure.color,
    },
    {
      key: 'monitoring',
      label: TOOL_GROUPS.monitoring.label,
      color: TOOL_GROUPS.monitoring.color,
    },
  ]

  const gradeOptions = ['A', 'B', 'C', 'D', 'F'] as const

  return (
    <div className='flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2'>
      {/* Group filter */}
      <div className='flex items-center gap-1.5 flex-wrap'>
        <span className='text-2xs font-bold uppercase tracking-wider text-muted-foreground leading-none mr-1'>
          Group
        </span>
        {groupOptions.map(opt => {
          const isActive = filters.group === opt.key
          return (
            <button
              key={opt.key}
              onClick={() => onFiltersChange({ ...filters, group: opt.key })}
              className='inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-all leading-none'
              style={{
                borderColor: isActive ? `${opt.color}66` : 'var(--border)',
                background: isActive ? `${opt.color}15` : 'transparent',
                color: isActive ? opt.color : 'var(--foreground)',
              }}
            >
              {opt.key !== 'all' && (
                <span
                  className='w-1.5 h-1.5 rounded-full'
                  style={{ background: opt.color }}
                  aria-hidden
                />
              )}
              {opt.label}
            </button>
          )
        })}
      </div>

      {/* Divider */}
      <span
        className='hidden sm:inline-block w-px h-4 bg-[var(--border)]'
        aria-hidden
      />

      {/* Grade filter */}
      <div className='flex items-center gap-1.5'>
        <span className='text-2xs font-bold uppercase tracking-wider text-muted-foreground leading-none mr-1'>
          Grade
        </span>
        {gradeOptions.map(g => {
          const isActive = filters.grade === g
          const color = GRADE_COLORS[g]
          return (
            <button
              key={g}
              onClick={() =>
                onFiltersChange({
                  ...filters,
                  grade: isActive ? null : g,
                })
              }
              className='inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold border transition-all leading-none'
              style={{
                borderColor: isActive ? `${color}66` : 'var(--border)',
                background: isActive ? `${color}15` : 'transparent',
                color: isActive ? color : 'var(--muted-foreground)',
              }}
              aria-pressed={isActive}
              aria-label={`Filter by grade ${g}`}
            >
              {g}
            </button>
          )
        })}
      </div>

      {/* Category chip — set by the summary cards, cleared here. */}
      {activeCategory && (
        <>
          <span
            className='hidden sm:inline-block w-px h-4 bg-[var(--border)]'
            aria-hidden
          />
          <button
            onClick={() => onFiltersChange({ ...filters, category: null })}
            className='inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 border transition-all leading-none text-primary'
            style={{
              borderColor:
                'color-mix(in srgb, var(--primary) 40%, transparent)',
            }}
            aria-label={`Clear the ${activeCategory.label} category filter`}
          >
            {activeCategory.label}
            <X size={11} aria-hidden />
          </button>
        </>
      )}

      {/* Status + clear */}
      <div className='ml-auto flex items-center gap-2'>
        {isFiltered ? (
          <>
            <span className='text-[11px] text-muted-foreground leading-none'>
              {matchedCount} of {totalCount} tools
            </span>
            <button
              onClick={() =>
                onFiltersChange({ group: 'all', grade: null, category: null })
              }
              className='inline-flex items-center gap-1 text-[11px] font-semibold text-foreground/80 hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted/50'
            >
              <X size={11} />
              Clear
            </button>
          </>
        ) : (
          <span className='text-[11px] text-muted-foreground leading-none'>
            {totalCount} tools
          </span>
        )}
      </div>
    </div>
  )
}
